/**
 * KycPanel — Account → ID verification section.
 *
 * Behavior:
 *  - "Start verification" opens the Stripe Identity hosted flow in a
 *    new tab. Status mutations happen via stripe-webhook only.
 *  - On return to the tab we call reload() to pull the new status.
 *  - Status copy follows the spec wording (no provider name surfaced
 *    in member-facing copy beyond the locked "Stripe Identity" mention
 *    in the trust line, per V2).
 */
import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useKyc } from "@/lib/safety/useKyc";
import { useProfile } from "@/lib/profile/useProfile";
import { toast } from "sonner";
import ScanAdapterPanel from "@/components/safety/ScanAdapterPanel";

const KycPanel = () => {
  const { view, start, reload } = useKyc();
  const { profile } = useProfile();
  const [busy, setBusy] = useState(false);

  // When the member returns to this tab, refresh status.
  useEffect(() => {
    const onFocus = () => void reload();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [reload]);

  const hasLegalName = !!(profile?.legal_first_name && profile?.legal_last_name);

  const handleStart = async () => {
    if (!hasLegalName) {
      toast.error("Add your legal first and last name first.");
      return;
    }
    setBusy(true);
    const returnUrl = `${window.location.origin}/account?kyc=return`;
    const { url, error } = await start(returnUrl);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const StatusBadge = () => {
    if (view.isVerified) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
          <ShieldCheck className="h-3.5 w-3.5" /> Verified
        </span>
      );
    }
    if (view.isPending) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reviewing
        </span>
      );
    }
    if (view.status === "requires_input") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive">
          <ShieldAlert className="h-3.5 w-3.5" /> Action needed
        </span>
      );
    }
    if (view.status === "canceled" || view.status === "rejected") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive">
          <ShieldAlert className="h-3.5 w-3.5" />
          {view.status === "canceled" ? "Canceled" : "Not approved"}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
        Not verified
      </span>
    );
  };

  const buttonLabel = (() => {
    if (view.isVerified) return "Re-verify";
    if (view.isPending) return "Resume verification";
    if (view.status === "requires_input") return "Continue verification";
    return "Start verification";
  })();

  return (
    <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">ID verification</h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-prose">
            Confirms you're a real person, 18+, and that your legal name matches
            your government ID. Required for verified-only features. Powered by
            Stripe Identity.
          </p>
        </div>
        <StatusBadge />
      </div>

      {!hasLegalName && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-xs text-destructive">
          Add your legal first and last name above before starting verification.
        </div>
      )}

      {view.isVerified && view.verifiedAt && (
        <p className="text-xs text-muted-foreground">
          Verified {new Date(view.verifiedAt).toLocaleDateString()}.
        </p>
      )}

      {view.isPending && (
        <p className="text-xs text-muted-foreground">
          We're reviewing your submission. This usually takes a few minutes.
          You'll see verified status here automatically.
        </p>
      )}

      {view.status === "requires_input" && (
        <p className="text-xs text-destructive">
          Something on the last submission needs attention. Continue to retry.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button onClick={handleStart} disabled={busy || !hasLegalName}>
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {!busy && <ExternalLink className="h-4 w-4 mr-1" />}
          {buttonLabel}
        </Button>
        {view.isPending && (
          <Button variant="outline" onClick={() => void reload()}>
            Refresh status
          </Button>
        )}
      </div>

      <div className="border-t border-border pt-5">
        <ScanAdapterPanel />
      </div>
    </section>
  );
};

export default KycPanel;
